import { openDB, DBSchema } from 'idb';
import { WorkOrder } from '../types/workOrders';
import { Expense } from '../types/expenses';
import { WorkerShift } from '../types/auth';

interface NinjasTimeDB extends DBSchema {
  offlinePhotos: {
    key: number;
    value: {
      id: number;
      reportId: number;
      taskItem: string;
      file: Blob;
      metadata: any;
      timestamp: number;
      status: 'pending' | 'uploading' | 'error';
      error?: string;
    };
  };
  workOrderDetails: {
    key: number;
    value: {
      reportId: number;
      jobNotes: any[];
      bidPhotos: any[];
      lastUpdated: number;
    };
  };
  workOrders: {
    key: number;
    value: WorkOrder;
  };
  expenses: {
    key: number;
    value: Expense;
  };
  shifts: {
    key: number;
    value: WorkerShift;
  };
  shiftSessions: {
    key: string;
    value: {
      worker_id: string;
      clockin_time: number;
      finishjob_time?: number;
      clockout_time?: number;
      status: 'working' | 'job_finished' | 'clocked_out';
    };
  };
  pendingActions: {
    key: number;
    value: {
      id?: number;
      type: string;
      data: any;
      timestamp: number;
    };
  };
  meta: {
    key: string;
    value: any;
  };
  categories: {
    key: number;
    value: {
      id: number;
      name: string;
    };
  };
}

let db: any;
let dbInitialized = false;

export async function initDB() {
  try {
    if (dbInitialized) return;

    db = await openDB<NinjasTimeDB>('ninjastime-db', 9, {
      upgrade(db, oldVersion, newVersion) {
        if (!db.objectStoreNames.contains('offlinePhotos')) {
          db.createObjectStore('offlinePhotos', { 
            keyPath: 'id',
            autoIncrement: true 
          });
        }

        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta');
        }

        if (!db.objectStoreNames.contains('workOrders')) {
          db.createObjectStore('workOrders', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('workOrderDetails')) {
          db.createObjectStore('workOrderDetails', { keyPath: 'reportId' });
        }

        if (!db.objectStoreNames.contains('expenses')) {
          db.createObjectStore('expenses', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('shifts')) {
          db.createObjectStore('shifts', { keyPath: 'clockin_time' });
        }

        if (!db.objectStoreNames.contains('shiftSessions')) {
          db.createObjectStore('shiftSessions', { keyPath: 'worker_id' });
        }

        if (!db.objectStoreNames.contains('pendingActions')) {
          db.createObjectStore('pendingActions', { keyPath: 'id', autoIncrement: true });
        }

        if (!db.objectStoreNames.contains('categories')) {
          db.createObjectStore('categories', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('categories')) {
          db.createObjectStore('categories', { keyPath: 'id' });
        }
      }
    });
    dbInitialized = true;
    console.log('IndexedDB initialized successfully');
  } catch (error) {
    console.error('Failed to initialize IndexedDB:', error);
    throw error;
  }
}

async function ensureDBInitialized() {
  if (!dbInitialized) {
    await initDB();
  }
}

// Work Orders
export async function saveWorkOrders(orders: WorkOrder[]) {
  await ensureDBInitialized();
  if (!db) {
    console.error('Database not initialized');
    return;
  }

  console.log('Saving work orders to IndexedDB:', orders.length);
  const tx = db.transaction('workOrders', 'readwrite');
  
  try {
    // Clear existing orders first
    await tx.store.clear();
    
    // Then add new orders
    for (const order of orders) {
      await tx.store.put(order);
    }
    
    await tx.done;
    console.log('Successfully saved work orders to IndexedDB');
  } catch (error) {
    console.error('Error saving work orders to IndexedDB:', error);
    throw error;
  }
}

export async function getWorkOrders(): Promise<WorkOrder[]> {
  await ensureDBInitialized();
  if (!db) {
    console.error('Database not initialized');
    return [];
  }

  try {
    console.log('Getting work orders from IndexedDB');
    const orders = await db.getAll('workOrders');
    console.log('Retrieved work orders:', orders?.length || 0);
    return orders || [];
  } catch (error) {
    console.error('Error getting work orders from IndexedDB:', error);
    return [];
  }
}

export async function saveWorkOrderDetails(reportId: number, jobNotes: any[], bidPhotos: any[] = []) {
  await ensureDBInitialized();
  const tx = db.transaction('workOrderDetails', 'readwrite');
  await tx.store.put({
    reportId,
    jobNotes,
    bidPhotos,
    lastUpdated: Date.now()
  });
  await tx.done;
}

export async function getWorkOrderDetails(reportId: number) {
  await ensureDBInitialized();
  return db.get('workOrderDetails', reportId);
}

// Expenses
export async function saveExpenses(expenses: Expense[]) {
  await ensureDBInitialized();
  const tx = db.transaction('expenses', 'readwrite');
  await tx.store.clear();
  await Promise.all([
    ...expenses.map(expense => tx.store.put(expense)),
    tx.done
  ]);
}

export async function getExpenses(): Promise<Expense[]> {
  await ensureDBInitialized();
  return db.getAll('expenses');
}

export async function getOfflineExpenses(): Promise<Expense[]> {
  return getExpenses();
}

// Categories
export async function saveCategories(categories: any[]) {
  await ensureDBInitialized();
  try {
    const tx = db.transaction('categories', 'readwrite');
    await tx.store.clear();
    for (const category of categories) {
      await tx.store.put(category);
    }
    await tx.done;
  } catch (error) {
    console.error('Error saving categories:', error);
    throw error;
  }
}

export async function getOfflineCategories() {
  await ensureDBInitialized();
  try {
    return db.getAll('categories') || [];
  } catch (error) {
    console.error('Error getting offline categories:', error);
    return [];
  }
}
// Shifts
export async function saveShifts(shifts: WorkerShift[]) {
  await ensureDBInitialized();
  const tx = db.transaction('shifts', 'readwrite');
  await Promise.all([
    ...shifts.map(shift => tx.store.put(shift)),
    tx.done
  ]);
}

export async function getShifts(): Promise<WorkerShift[]> {
  await ensureDBInitialized();
  return db.getAll('shifts');
}

// Shift Sessions
export async function saveShiftSession(session: {
  worker_id: string;
  clockin_time: number;
  finishjob_time?: number;
  clockout_time?: number;
  status: 'working' | 'job_finished' | 'clocked_out';
}) {
  await ensureDBInitialized();
  // Add to pending actions if offline
  if (!navigator.onLine) {
    await addPendingAction({
      type: session.status === 'working' ? 'clockIn' : 
            session.status === 'job_finished' ? 'finishJob' :
            'clockOut',
      data: session
    });
  }
  return db.put('shiftSessions', session);
}

export async function getShiftSession(workerId: string) {
  await ensureDBInitialized();
  return db.get('shiftSessions', workerId);
}

export async function updateShiftSession(workerId: string, updates: Partial<{
  finishjob_time: number;
  clockout_time: number;
  status: 'working' | 'job_finished' | 'clocked_out';
}>) {
  await ensureDBInitialized();
  const session = await getShiftSession(workerId);
  if (session) {
    const updatedSession = { ...session, ...updates };
    // Add to pending actions if offline
    if (!navigator.onLine) {
      await addPendingAction({
        type: updates.status === 'job_finished' ? 'finishJob' : 'clockOut',
        data: updatedSession
      });
    }
    return db.put('shiftSessions', updatedSession);
  }
}

export async function removeShiftSession(workerId: string) {
  await ensureDBInitialized();
  return db.delete('shiftSessions', workerId);
}
export async function getCurrentShift(): Promise<WorkerShift | null> {
  await ensureDBInitialized();
  const shifts = await getShifts();
  return shifts.find(shift => !shift.clockout_time) || null;
}

export async function updateShift(shift: WorkerShift) {
  await ensureDBInitialized();
  return db.put('shifts', shift);
}

// Pending Actions
export async function addPendingAction(action: { type: string; data: any }) {
  await ensureDBInitialized();
  
  // Check for duplicate actions
  const existingActions = await getPendingActions();
  const isDuplicate = existingActions.some(
    existing => 
      existing.type === action.type && 
      existing.data?.worker_id === action.data?.worker_id &&
      Math.abs(existing.timestamp - Date.now()) < 1000 // Within 1 second
  );

  if (isDuplicate) {
    console.log('Skipping duplicate action:', action);
    return;
  }

  return db.add('pendingActions', {
    ...action,
    timestamp: Date.now()
  });
}

export async function getPendingActions() {
  await ensureDBInitialized();
  return db.getAll('pendingActions');
}

export async function removePendingAction(id: number) {
  await ensureDBInitialized();
  try {
    await db.delete('pendingActions', id);
    return true;
  } catch (error) {
    console.error('Failed to remove pending action:', error);
    throw new Error('Failed to remove action');
  }
}

// Meta Data
export async function updateLastSyncTimestamp() {
  await ensureDBInitialized();
  return db.put('meta', Date.now(), 'lastSync');
}

export async function getLastSyncTimestamp(): Promise<number> {
  await ensureDBInitialized();
  return db.get('meta', 'lastSync') || 0;
}

export async function clearOldData() {
  await ensureDBInitialized();
  try {
    // Keep pending actions but clear all other data
    const stores = ['workOrders', 'workOrderDetails', 'expenses', 'shifts'];
    
    // Clear each store individually to handle errors gracefully
    for (const storeName of stores) {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      await store.clear();
      await tx.done;
    }
  } catch (error) {
    console.error('Error clearing old data:', error);
    throw new Error('Failed to clear old data');
  }
}

// Offline Photos
export async function saveOfflinePhoto(
  reportId: number,
  taskItem: string,
  file: Blob,
  metadata: any
): Promise<number> {
  await ensureDBInitialized();
  const id = await db.add('offlinePhotos', {
    reportId,
    taskItem,
    file,
    metadata,
    timestamp: Date.now(),
    status: 'pending'
  });
  return id;
}

export async function getOfflinePhotos(reportId: number) {
  await ensureDBInitialized();
  const allPhotos = await db.getAll('offlinePhotos');
  return allPhotos.filter(photo => photo.reportId === reportId);
}

export async function getOfflinePhoto(id: number) {
  await ensureDBInitialized();
  return db.get('offlinePhotos', id);
}

export async function removeOfflinePhoto(id: number) {
  await ensureDBInitialized();
  return db.delete('offlinePhotos', id);
}

export async function updateOfflinePhotoStatus(
  id: number, 
  status: 'pending' | 'uploading' | 'error',
  error?: string
) {
  await ensureDBInitialized();
  const photo = await db.get('offlinePhotos', id);
  if (photo) {
    return db.put('offlinePhotos', {
      ...photo,
      status,
      error
    });
  }
}